import React from 'react';
import type { FeeDraftFields, FeeDraftErrors } from './feeConfigDraft';

const INPUT = 'w-full px-3 py-2.5 bg-stone-50 border rounded-lg text-sm text-stone-800 outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 min-h-[44px] disabled:opacity-50';

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; disabled?: boolean; error?: string }> = ({ label, value, onChange, disabled, error }) => (
  <div>
    <label className="block text-xs font-medium text-stone-600 mb-1">{label}</label>
    <input
      type="text"
      inputMode="decimal"
      value={value}
      disabled={disabled}
      aria-invalid={!!error}
      onChange={(e) => onChange(e.target.value)}
      className={`${INPUT} ${error ? 'border-red-400' : 'border-stone-200'}`}
    />
    {error && <div className="mt-1 text-[11px] text-red-600">{error}</div>}
  </div>
);

/**
 * ACH % + flat, Card % + flat, Min, Max / No max -- the numeric fee fields shared by the fee-plan
 * editor and the fallback fee config (final review C-I3, C-I7). Errors come from parseFeeDraft.
 */
export const FeeFieldsEditor: React.FC<{
  value: FeeDraftFields;
  onChange: (v: FeeDraftFields) => void;
  errors: FeeDraftErrors;
}> = ({ value, onChange, errors }) => {
  const set = (patch: Partial<FeeDraftFields>) => onChange({ ...value, ...patch });
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="ACH %" value={value.achPct} onChange={(v) => set({ achPct: v })} error={errors.achPct} />
        <Field label="ACH flat $" value={value.achFlat} onChange={(v) => set({ achFlat: v })} error={errors.achFlat} />
        <Field label="Card %" value={value.cardPct} onChange={(v) => set({ cardPct: v })} error={errors.cardPct} />
        <Field label="Card flat $" value={value.cardFlat} onChange={(v) => set({ cardFlat: v })} error={errors.cardFlat} />
        <Field label="Min $" value={value.min} onChange={(v) => set({ min: v })} error={errors.min} />
        <Field label="Max $" value={value.max} onChange={(v) => set({ max: v })} disabled={value.noMax} error={value.noMax ? undefined : errors.max} />
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value.noMax}
        onClick={() => set({ noMax: !value.noMax })}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold min-h-[44px] ${value.noMax ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-stone-300 bg-white text-stone-500'}`}
      >
        No max
      </button>
    </>
  );
};

export default FeeFieldsEditor;
